import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AuthService } from '../../../shared/services/auth.service';
import { FileTransferService } from '../../../shared/services/signed-url';
import { MTMFileDownload } from '../../../shared/services/signed-url/mtm-file-download';
import { SubsectionService } from '../../../shared/services/subsection.service';
import {
  TranscodeMonitorResult,
  TranscodeMonitorService,
  TranscodeMonitorType,
} from '../../../shared/services/transcode-monitor.service';
import { Subject, takeUntil } from 'rxjs';
import { EmitterService } from '../../../shared/services/emitter.service';
import { FileTranscodedEvent } from '../../../shared/models';
import { ProjectService } from '../../../shared/services/project.service';
import { TopMediaInfoService } from '../../../shared/components/top-media-info/top-media-info.service';
import {
  ActiveSubsectionState,
  SimpleFilePage,
  SimpleFilePages,
  SubsectionNavigationParams,
} from '../../../shared/interfaces';

@Component({
  selector: 'subsection-page-list',
  templateUrl: './subsection-page-list.component.html',
  styleUrl: './subsection-page-list.component.scss',
  standalone: false,
})
export class SubsectionPageListComponent implements OnInit, OnDestroy {
  isReady: boolean = false;
  private _filePages: SimpleFilePages;

  @Input() isWidgetMode: boolean = false;
  @Input() subsectionState: ActiveSubsectionState | null;
  @Input() isReviewLinkPage: boolean = false;

  get filePages(): SimpleFilePages {
    return this._filePages;
  }

  @Input()
  set filePages(value: SimpleFilePages) {
    this._filePages = value;
    if (value) {
      this.isReady = this.filePages.pages?.every((p) => p.status === 'READY');
    } else {
      this.isReady = false;
    }
    if (!this.isReady && this.filePages?.pages?.length > 0) {
      if (this.fileIdToMonitor) {
        this.unmonitorFile(this.fileIdToMonitor);
      }
      this.monitorFileReadiness(this.filePages.fileId);
    }
  }

  @Output() pageClick = new EventEmitter<Partial<ActiveSubsectionState>>();

  private readonly projectService = inject(ProjectService);
  private readonly subsectionService = inject(SubsectionService);
  private readonly authService = inject(AuthService);
  private readonly transferService = inject(FileTransferService);
  private readonly transcodeMonitorService = inject(TranscodeMonitorService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  commentCount: Record<string, number> = {};

  itemSize = 150; // Default item height
  itemsPerRow = 4; // Default number of items per row
  fileIdToMonitor: string;
  private readonly ngUnsubscribe = new Subject<void>();

  ngOnInit() {
    console.log('ngOnInit', this.topMediaInfoService.showListViewModeButton);
    this.topMediaInfoService.updateState({ showListViewModeButton: false });
    this.subsectionService.commentCount$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (commentCount) => {
        this.commentCount = commentCount;
      },
    });
  }

  ngOnDestroy() {
    if (this.fileIdToMonitor) {
      this.unmonitorFile(this.fileIdToMonitor);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  trackByPageId(index: number, page: SimpleFilePages) {
    return page.fileId;
  }

  selectPage(page: SimpleFilePage) {
    this.pageClick.emit({
      ...this.subsectionState,
      activePage: page,
    });

    if (!this.isWidgetMode) {
      const params: SubsectionNavigationParams = {
        projectId: this.projectService.project?.id!,
        sectionId: this.subsectionState?.section?.id!,
        subsectionId: this.subsectionState?.subsection?.id!,
        topicId: this.subsectionState?.activeTopic?.topicId,
        optionId: this.subsectionState?.activeOption?.optionId,
        versionNumber: this.subsectionState?.activeVersion?.versionNumber,
        fileId: this.subsectionState?.activeFile?.id,
        page: page.rank,
      };
      !this.isReviewLinkPage
        ? this.subsectionService.navigateSubsection(params)
        : this.subsectionService.updateSubsectionState({ activePage: page });
    }
  }

  downloadPage(page: SimpleFilePage) {
    const downloadItem = new MTMFileDownload();
    downloadItem.name = page.name;
    downloadItem.url = page.signedURL;
    downloadItem.contentType = 'image/png';
    downloadItem.displayName = page.name;
    this.transferService.downloadFile(downloadItem);
  }

  private calculateItemHeight(): number {
    return 180; // Adjust based on your design needs
  }

  hasCommentCount(key: string): boolean {
    return key in this.commentCount && this.commentCount[key] > 0;
  }

  private unmonitorFile(fileId: string) {
    this.transcodeMonitorService.stopMonitoringFile(fileId);
  }

  private monitorFileReadiness(fileId: string) {
    this.fileIdToMonitor = fileId;
    const obs$ = this.transcodeMonitorService.monitorFile({
      projectId: this.projectService.project?.id,
      fileId: fileId,
      codec: 'images',
      type: TranscodeMonitorType.Pages,
    });
    obs$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data: TranscodeMonitorResult) => {
        if (!data?.file) {
          return;
        }

        const isSameFile = data.fileId == this.fileIdToMonitor;

        if (data.isSuccess && isSameFile) {
          EmitterService.get(FileTranscodedEvent).emit(data);
        }
      },
    });
  }
}
